// Dimension Configuration Management
import { db } from './firebase';
import { collection, doc, setDoc, getDoc, getDocs, updateDoc, deleteDoc, serverTimestamp, query, where } from 'firebase/firestore';

// Default dimension templates for common product types
export const dimensionTemplates = {
  lampshade_round: {
    name: 'Round Lampshade',
    dimensions: {
      topDiameter: { 
        label: 'Top Diameter', 
        required: true, 
        unit: 'cm', 
        min: 10, 
        max: 100,
        placeholder: 'e.g., 30',
        description: 'Diameter at the top opening'
      },
      bottomDiameter: { 
        label: 'Bottom Diameter', 
        required: true, 
        unit: 'cm', 
        min: 15, 
        max: 120,
        placeholder: 'e.g., 40',
        description: 'Diameter at the bottom opening'
      },
      height: { 
        label: 'Height', 
        required: true, 
        unit: 'cm', 
        min: 10, 
        max: 80,
        placeholder: 'e.g., 25',
        description: 'Vertical height of the shade'
      },
      slope: { 
        label: 'Slope Angle', 
        required: false, 
        unit: 'degrees',
        min: 0,
        max: 45,
        placeholder: 'e.g., 15',
        description: 'Angle of the shade slope (optional)'
      }
    }
  },
  lampshade_square: {
    name: 'Square/Rectangular Lampshade',
    dimensions: {
      topWidth: { 
        label: 'Top Width', 
        required: true, 
        unit: 'cm', 
        min: 10, 
        max: 100,
        placeholder: 'e.g., 30'
      },
      topDepth: { 
        label: 'Top Depth', 
        required: true, 
        unit: 'cm', 
        min: 10, 
        max: 100,
        placeholder: 'e.g., 30'
      },
      bottomWidth: { 
        label: 'Bottom Width', 
        required: true, 
        unit: 'cm', 
        min: 15, 
        max: 120,
        placeholder: 'e.g., 40'
      },
      bottomDepth: { 
        label: 'Bottom Depth', 
        required: true, 
        unit: 'cm', 
        min: 15, 
        max: 120,
        placeholder: 'e.g., 40'
      },
      height: { 
        label: 'Height', 
        required: true, 
        unit: 'cm', 
        min: 10, 
        max: 80,
        placeholder: 'e.g., 25'
      }
    }
  },
  chair: {
    name: 'Chair',
    dimensions: {
      seatWidth: { 
        label: 'Seat Width', 
        required: true, 
        unit: 'cm', 
        min: 30, 
        max: 80,
        placeholder: 'e.g., 45'
      },
      seatDepth: { 
        label: 'Seat Depth', 
        required: true, 
        unit: 'cm', 
        min: 30, 
        max: 60,
        placeholder: 'e.g., 40'
      },
      seatHeight: { 
        label: 'Seat Height', 
        required: true, 
        unit: 'cm', 
        min: 35, 
        max: 55,
        placeholder: 'e.g., 45'
      },
      backHeight: { 
        label: 'Back Height', 
        required: true, 
        unit: 'cm', 
        min: 30, 
        max: 120,
        placeholder: 'e.g., 80',
        description: 'Height from seat to top of back'
      },
      armHeight: { 
        label: 'Arm Height', 
        required: false, 
        unit: 'cm', 
        min: 15, 
        max: 30,
        placeholder: 'e.g., 20',
        description: 'Height of armrests from seat (if applicable)'
      }
    }
  }
};

// Get dimension configuration for a specific taxonomy path (check hierarchy)
export const getDimensionConfig = async (taxonomyPath) => {
  try {
    // First check for direct configuration in Firebase
    const configRef = doc(db, 'dimensionConfigs', taxonomyPath.replace(/\./g, '_'));
    const configDoc = await getDoc(configRef);
    
    if (configDoc.exists()) {
      const data = configDoc.data();
      // If it has a templateKey, verify the template still exists
      if (data.templateKey) {
        const allTemplates = await getAllDimensionTemplates();
        if (allTemplates[data.templateKey]) {
          // Template exists, return the config
          return {
            ...data,
            inheritedFrom: null // This is a direct config, not inherited
          };
        } else {
          // Template no longer exists, return the config but mark it
          console.warn('Template no longer exists for:', taxonomyPath, 'template:', data.templateKey);
          return {
            ...data,
            inheritedFrom: null,
            templateMissing: true
          };
        }
      }
      // No template key, just return the config
      return {
        ...data,
        inheritedFrom: null
      };
    }
    
    // No direct config, check parent paths for inheritance
    const pathParts = taxonomyPath.split('.');
    
    // Walk up the hierarchy looking for a parent with configuration
    for (let i = pathParts.length - 1; i > 0; i--) {
      const parentPath = pathParts.slice(0, i).join('.');
      const parentConfigRef = doc(db, 'dimensionConfigs', parentPath.replace(/\./g, '_'));
      const parentConfigDoc = await getDoc(parentConfigRef);
      
      if (parentConfigDoc.exists()) {
        const parentData = parentConfigDoc.data();
        // Found a parent with config, return it as inherited
        return {
          ...parentData,
          path: taxonomyPath,
          inheritedFrom: parentPath
        };
      }
    }
    
    // No configuration found anywhere in the hierarchy
    return null;
  } catch (error) {
    console.error('Error getting dimension config:', error);
    return null;
  }
};

// Save dimension configuration for a taxonomy path
export const saveDimensionConfig = async (taxonomyPath, config) => {
  try {
    const configId = taxonomyPath.replace(/\./g, '_');
    const configRef = doc(db, 'dimensionConfigs', configId);
    
    await setDoc(configRef, {
      ...config,
      path: taxonomyPath,
      id: configId,
      updatedAt: serverTimestamp(),
      inheritedFrom: null // Clear inheritance when explicitly setting
    });
    
    return true;
  } catch (error) {
    console.error('Error saving dimension config:', error);
    throw error;
  }
};

// Get all dimension configurations
export const getAllDimensionConfigs = async () => {
  try {
    const snapshot = await getDocs(collection(db, 'dimensionConfigs'));
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting all dimension configs:', error);
    return [];
  }
};

// Delete dimension configuration
export const deleteDimensionConfig = async (configId) => {
  try {
    await deleteDoc(doc(db, 'dimensionConfigs', configId));
    return true;
  } catch (error) {
    console.error('Error deleting dimension config:', error);
    throw error;
  }
};

// Apply template to a taxonomy path
export const applyDimensionTemplate = async (taxonomyPath, templateKey) => {
  try {
    // Get all templates including user-created ones
    const allTemplates = await getAllDimensionTemplates();
    const template = allTemplates[templateKey];
    
    if (!template) {
      throw new Error(`Template "${templateKey}" not found`);
    }
    
    const config = {
      name: template.name,
      dimensions: template.dimensions,
      templateKey: templateKey,
      inheritedFrom: null // Clear any inheritance when explicitly applying a template
    };
    
    // Force overwrite any existing configuration
    return await saveDimensionConfig(taxonomyPath, config);
  } catch (error) {
    console.error('Error applying dimension template:', error);
    throw error;
  }
};

// Validate dimension values
export const validateDimensions = (dimensions, config) => {
  const errors = {};
  const warnings = {};
  
  if (!config || !config.dimensions) {
    return { valid: false, errors: { general: 'No dimension configuration found' }, warnings: {} };
  }
  
  // Check required fields and values
  Object.entries(config.dimensions).forEach(([key, dimConfig]) => {
    const value = dimensions[key];
    
    if (dimConfig.required && (!value || value === '')) {
      errors[key] = `${dimConfig.label} is required`;
    } else if (value) {
      const numValue = parseFloat(value);
      
      if (isNaN(numValue)) {
        errors[key] = `${dimConfig.label} must be a number`;
      } else {
        // Check if value is 10x larger or smaller than expected range
        if (dimConfig.min !== undefined || dimConfig.max !== undefined) {
          const expectedMin = dimConfig.min || 0;
          const expectedMax = dimConfig.max || expectedMin * 10; // fallback if only min is set
          
          // Value is more than 10x the expected max
          if (numValue > expectedMax * 10) {
            warnings[key] = `Hmm that's an odd dimension (${numValue}${dimConfig.unit}), are you sure? Expected around ${expectedMin}-${expectedMax}${dimConfig.unit}`;
          }
          // Value is more than 10x smaller than expected min (and min > 0)
          else if (expectedMin > 0 && numValue < expectedMin / 10) {
            warnings[key] = `Hmm that's an odd dimension (${numValue}${dimConfig.unit}), are you sure? Expected around ${expectedMin}-${expectedMax}${dimConfig.unit}`;
          }
        }
      }
    }
  });
  
  return {
    valid: Object.keys(errors).length === 0,
    errors,
    warnings
  };
};

// Format dimensions for display
export const formatDimensions = (dimensions, config) => {
  if (!dimensions || !config || !config.dimensions) {
    return 'No dimensions specified';
  }
  
  const formatted = [];
  Object.entries(dimensions).forEach(([key, value]) => {
    if (value && config.dimensions[key]) {
      const dimConfig = config.dimensions[key];
      formatted.push(`${dimConfig.label}: ${value}${dimConfig.unit}`);
    }
  });
  
  return formatted.join(', ') || 'No dimensions specified';
};

// Template Management Functions

// Get all dimension templates (both hardcoded and user-created)
export const getAllDimensionTemplates = async () => {
  try {
    // Get user-created templates from Firestore
    const snapshot = await getDocs(collection(db, 'dimensionTemplates'));
    const userTemplates = {};
    snapshot.docs.forEach(doc => {
      userTemplates[doc.id] = {
        id: doc.id,
        ...doc.data(),
        isUserCreated: true
      };
    });
    
    // Merge with hardcoded templates
    const allTemplates = {};
    Object.entries(dimensionTemplates).forEach(([key, template]) => {
      allTemplates[key] = {
        ...template,
        id: key,
        isUserCreated: false
      };
    });
    
    // User templates override hardcoded ones with same key
    Object.assign(allTemplates, userTemplates);
    
    return allTemplates;
  } catch (error) {
    console.error('Error getting all dimension templates:', error);
    return dimensionTemplates;
  }
};

// Save a new or updated dimension template
export const saveDimensionTemplate = async (templateKey, template) => {
  try {
    const templateRef = doc(db, 'dimensionTemplates', templateKey);
    
    await setDoc(templateRef, {
      ...template,
      id: templateKey,
      updatedAt: serverTimestamp(),
      isUserCreated: true
    });
    
    return true;
  } catch (error) {
    console.error('Error saving dimension template:', error);
    throw error;
  }
};

// Delete a user-created dimension template
export const deleteDimensionTemplate = async (templateKey) => {
  try {
    // Only allow deletion of user-created templates
    const templateRef = doc(db, 'dimensionTemplates', templateKey);
    const templateDoc = await getDoc(templateRef);
    
    if (!templateDoc.exists()) {
      throw new Error('Cannot delete hardcoded template');
    }
    
    await deleteDoc(templateRef);
    return true;
  } catch (error) {
    console.error('Error deleting dimension template:', error);
    throw error;
  }
};

// Create a new template from an existing configuration
export const createTemplateFromConfig = async (templateKey, templateName, config) => {
  try {
    const template = {
      name: templateName,
      dimensions: config.dimensions || {},
      createdAt: serverTimestamp()
    };
    
    return await saveDimensionTemplate(templateKey, template);
  } catch (error) {
    console.error('Error creating template from config:', error);
    throw error;
  }
};

// Functions to support taxonomy integration

// Save dimension configuration directly to taxonomy item
export const saveTaxonomyDimensions = async (taxonomyPath, dimensionData) => {
  try {
    // Save to the dimensionConfigs collection using taxonomy path
    return await saveDimensionConfig(taxonomyPath, dimensionData);
  } catch (error) {
    console.error('Error saving taxonomy dimensions:', error);
    throw error;
  }
};

// Delete dimension configuration for a taxonomy item
export const deleteTaxonomyDimensions = async (taxonomyPath) => {
  try {
    const configId = taxonomyPath.replace(/\./g, '_');
    return await deleteDimensionConfig(configId);
  } catch (error) {
    console.error('Error deleting taxonomy dimensions:', error);
    throw error;
  }
};

// Get dimension info for taxonomy tree display
export const getDimensionInfo = async (taxonomyPath) => {
  try {
    // First check if there's a direct configuration in Firebase
    const configId = taxonomyPath.replace(/\./g, '_');
    const configRef = doc(db, 'dimensionConfigs', configId);
    const configDoc = await getDoc(configRef);
    
    if (configDoc.exists()) {
      const data = configDoc.data();
      return {
        hasConfig: true,
        inheritedFrom: data.inheritedFrom || null,
        templateKey: data.templateKey || null,
        name: data.name || 'Custom Configuration',
        dimensionCount: Object.keys(data.dimensions || {}).length
      };
    }
    
    // If no direct config, check for inherited template-based config
    const config = await getDimensionConfig(taxonomyPath);
    if (!config) {
      return { hasConfig: false, inheritedFrom: null, templateKey: null };
    }
    
    return {
      hasConfig: true,
      inheritedFrom: config.inheritedFrom,
      templateKey: config.templateKey || null,
      name: config.name || 'Custom Configuration',
      dimensionCount: Object.keys(config.dimensions || {}).length
    };
  } catch (error) {
    console.error('Error getting dimension info:', error);
    return { hasConfig: false, inheritedFrom: null, templateKey: null };
  }
};